import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { ElemAttrs } from '../../utils/HtmlAttrs';
import { clamp } from '../../utils/Math';
import { SkeletonLoaderInfoContext } from './SkeletonLoaderInfoContext';
import { getBackgroundColor, getShineColor, LoadingBoneStyleName, ShineAnimationDurationMs, ShineStyleName } from './SkeletonLoaderStyles';

const PostShineDelayMs = 600;

/**
 * A placeholder element used to indicate content will show up, once an asynchronous task has completed.
 * All `<LoadingBone/>`s whose visibility is controlled by the same asynchronous request,
 * should be nested under the same parent <SkeletonLoaderWidget />.
 */
export const LoadingBone: React.FC<{ children?: never; } & ElemAttrs> = props => {
  const { className, style, ...restProps } = props;

  /**
   * Offset in pixels from the left edge of the parentEl, where this component is.
   * This is used to make it look like the shine transitions across the entire page.
   */
  const [xOffset, setXOffset] = React.useState<number>(0);
  const elementRef = React.useRef<HTMLDivElement | null>(null);
  const [shineWidth, setShineWidth] = React.useState<number>(80);
  const { clientRect, darkMode, isInitialized } = React.useContext(SkeletonLoaderInfoContext);
  /** We add an extra 600ms to the duration, before the animation starts again */
  const pixelsPerSecond = clientRect.width / ((ShineAnimationDurationMs - PostShineDelayMs) / 1000);
  /**
   * At what point in the animation, does it complete, allowing for 600ms of additional rest time.
   * @default ```0.7```
   */
  const animationCompletePercentage = clientRect.width / (clientRect.width + (pixelsPerSecond * PostShineDelayMs / 1000)) || .7;

  React.useEffect(() => {
    const selfLeft = elementRef.current?.getBoundingClientRect().left || 0;
    setXOffset(selfLeft - clientRect.left);
    const newWidth = (elementRef.current?.getBoundingClientRect().width || 0) * .20;
    setShineWidth(clamp(newWidth, 80, 200));
  }, [clientRect.width, clientRect.left]);

  const SkeletonLoaderShineAnimation = React.useMemo(() => {
    return TypeStyle.keyframes({
      '0%': {
        left: '0',
        marginLeft: -shineWidth,
      },
      [`${animationCompletePercentage * 100}%`]: {
        left: clientRect.width,
        marginLeft: 0,
      },
      '100%': {
        left: clientRect.width,
        marginLeft: 0,
      },
    });
  }, [animationCompletePercentage, clientRect.width, shineWidth]);

  const getDynamicStyle = (): React.CSSProperties => ({
    backgroundColor: getBackgroundColor(darkMode ? .4 : 1),
  });

  const getShineStyle = (): React.CSSProperties => ({
    animationName: SkeletonLoaderShineAnimation,
    animationDelay: `${(xOffset / pixelsPerSecond) * 1000}ms`,
    background: `linear-gradient(to right, transparent, ${getShineColor(darkMode ? .6 : 1)}, transparent)`,
    marginLeft: -shineWidth,
    width: shineWidth,
  });

  return (
    <div
      className={TypeStyle.classes(LoadingBoneStyleName, className)}
      {...restProps}
      children={<div className={ShineStyleName} style={{ ...(isInitialized && getShineStyle()) }} />}
      ref={elementRef}
      style={{ ...style, ...getDynamicStyle() }}
    />
  );
};
LoadingBone.displayName = 'LoadingBone';
